import { createEffect, createMemo, createResource, createSignal, For, Index, onCleanup, Show } from "solid-js";
import { useApi, isOk } from "../../api";
import { firstError } from "../../api/envelope";
import { useActiveBot } from "../../state/activeBot";
import BotScoped from "../../components/BotScoped";
import Text from "../../components/Text";
import Modal from "../../components/Modal";
import { showConfirm } from "../../components/Dialog";
import { Badge } from "../../components/Badge";
import { EmptyState } from "../../components/EmptyState";
import { useToast } from "../../components/MessageBox";
import { useTranslation } from "../../hooks/useTranslation";
import { formatDateTime, formatDateTimeShort } from "../../services/dateFormat";
import ConditionTreeEditor, { emptyGroup, type ConditionGroup, type ConditionParam } from "../../components/ConditionTreeEditor";
import EventPicker from "../../components/EventPicker";
import TaskParameterInput from "../../components/TaskParameterInput";
import PlaceholderChip from "../../components/PlaceholderChip";
import ScheduleEditor, { defaultSchedule, describeSchedule, parseSchedule, scheduleToJson, type ScheduleDraft } from "../../components/ScheduleEditor";
import { DateRangePills } from "../../components/DateRangePills";
import { decodeShareCode, encodeShareCode } from "../../services/handlerShareCode";
import type {
    EventCatalogResponse,
    EventHandlerDto,
    EventHandlerActionRequest,
    EventHandlerDryRunResponse,
    WebhookDto,
} from "../../api/generated/schemas";
import { EventActionType } from "../../api/generated/schemas";
import styles from "./Automation.module.css";

const ACTION_WEBHOOK = EventActionType.NUMBER_0;
const ACTION_RUN_TASK = EventActionType.NUMBER_1;

const TASK_LIFECYCLE_EVENTS = new Set(["task_completed", "task_failed", "task_cancelled"]);
const SCHEDULE_CATEGORY = "schedule";

type Trigger = "event" | "schedule";

interface HandlerSource {
    id: string | null;
    name: string;
    eventKey: string;
    isEnabled: boolean;
    cooldownSeconds: number;
    runWhilePossessed: boolean;
    conditionJson: string;
    scheduleJson: string;
    actions: { actionType: EventActionType; webhookId: string | null; webhookName: string | null; configJson: string }[];
}

// Single quotes inside the placeholder, so a string argument never collides with the double quotes
// of the JSON body around it. A backslash is not an escape in the template language.
const DEFAULT_DISCORD_BODY = '{"content":"{payer} paid {currency(amount, \'$\')}"}';

interface DraftEmbedField {
    name: string;
    value: string;
    inline: boolean;
}

interface DraftAction {
    actionType: EventActionType;
    webhookId: string | null;
    useEmbed: boolean;
    content: string;
    title: string;
    description: string;
    url: string;
    color: string;
    fields: DraftEmbedField[];
    authorName: string;
    authorUrl: string;
    authorIconUrl: string;
    thumbnailUrl: string;
    imageUrl: string;
    footerText: string;
    footerIconUrl: string;
    timestamp: boolean;
    body: string;
    plugin: string;
    task: string;
    startImmediately: boolean;
    args: Record<string, string>;
    missingWebhookName: string | null;
}

type EmbedTextKey = "authorName" | "authorUrl" | "authorIconUrl" | "thumbnailUrl" | "imageUrl" | "footerText" | "footerIconUrl";

interface Draft {
    id: string | null;
    name: string;
    eventKey: string;
    isEnabled: boolean;
    cooldownSeconds: number;
    runWhilePossessed: boolean;
    condition: ConditionGroup;
    schedule: ScheduleDraft;
    actions: DraftAction[];
    imported: boolean;
}

const emptyAction = (): DraftAction => ({
    actionType: ACTION_WEBHOOK,
    webhookId: null,
    useEmbed: false,
    content: "",
    title: "",
    description: "",
    url: "",
    color: "#5865F2",
    fields: [],
    authorName: "",
    authorUrl: "",
    authorIconUrl: "",
    thumbnailUrl: "",
    imageUrl: "",
    footerText: "",
    footerIconUrl: "",
    timestamp: false,
    body: DEFAULT_DISCORD_BODY,
    plugin: "",
    task: "",
    startImmediately: false,
    args: {},
    missingWebhookName: null,
});

const emptyDraft = (eventKey: string): Draft => ({
    id: null,
    name: "",
    eventKey,
    isEnabled: true,
    cooldownSeconds: 0,
    runWhilePossessed: false,
    condition: emptyGroup(),
    schedule: defaultSchedule(),
    actions: [emptyAction()],
    imported: false,
});

export default function AutomationPage() {
    return <BotScoped><AutomationInner /></BotScoped>;
}

function AutomationInner() {
    const api = useApi();
    const { t } = useTranslation();
    const toast = useToast();
    const { botId: activeBotId } = useActiveBot();
    const botId = () => activeBotId() ?? "";

    const [formOpen, setFormOpen] = createSignal(false);
    const [saving, setSaving] = createSignal(false);
    const [draft, setDraft] = createSignal<Draft>(emptyDraft(""));
    const [dryRun, setDryRun] = createSignal<EventHandlerDryRunResponse | null>(null);
    const [samples, setSamples] = createSignal<Record<string, string>>({});
    const [lastEventKey, setLastEventKey] = createSignal("");
    const [sharing, setSharing] = createSignal<EventHandlerDto | null>(null);
    const [importOpen, setImportOpen] = createSignal(false);
    const [importCode, setImportCode] = createSignal("");
    const [importError, setImportError] = createSignal<string | null>(null);

    const [catalogResponse] = createResource(botId, async (id): Promise<EventCatalogResponse> => {
        const res = await api.eventHandler.catalog(id);
        return isOk(res.data) ? res.data.value! : { events: [], botParameters: [] };
    });

    const catalog = () => catalogResponse()?.events;
    const botParams = () => catalogResponse()?.botParameters ?? [];
    const pickableEvents = createMemo(() => (catalog() ?? []).filter(e => e.category !== SCHEDULE_CATEGORY));
    const scheduleEventKey = () => catalog()?.find(e => e.category === SCHEDULE_CATEGORY)?.key ?? "";
    const isScheduled = (eventKey: string) => eventKey !== "" && eventKey === scheduleEventKey();

    const [webhooks] = createResource(async () => {
        const res = await api.webhook.list();
        return isOk(res.data) ? res.data.value!.webhooks : ([] as WebhookDto[]);
    });

    const [tasks] = createResource(botId, async (id) => {
        const res = await api.botTask.getAvailableTasks(id);
        return isOk(res.data) ? res.data.value!.available : [];
    });

    const [handlers, { refetch }] = createResource(botId, async (id) => {
        const res = await api.eventHandler.list(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t("AUTOMATION_LOAD_FAILED"));
            return [] as EventHandlerDto[];
        }
        return res.data.value!.handlers;
    });

    const [executions, { refetch: refetchLog }] = createResource(botId, async (id) => {
        const res = await api.eventHandler.executions(id, { limit: 25 });
        return isOk(res.data) ? res.data.value!.executions : [];
    });

    const definitionFor = (eventKey: string) => catalog()?.find(d => d.key === eventKey);

    const eventParams = createMemo<ConditionParam[]>(() =>
        (definitionFor(draft().eventKey)?.parameters ?? []).map(p => ({ key: p.key, type: p.type, options: p.options, optionSource: p.optionSource })));

    const conditionParams = createMemo<ConditionParam[]>(() =>
        [...eventParams(), ...botParams().map(p => ({ key: p.key, type: p.type, options: p.options }))]);

    const draftScheduled = () => isScheduled(draft().eventKey);

    const loopWarning = () =>
        TASK_LIFECYCLE_EVENTS.has(draft().eventKey) && draft().actions.some(a => a.actionType === ACTION_RUN_TASK);

    const openDraft = (next: Draft) => {
        setDraft(next);
        setLastEventKey(isScheduled(next.eventKey) ? pickableEvents()[0]?.key ?? "" : next.eventKey);
        setSamples({});
        setDryRun(null);
        setFormOpen(true);
    };

    const openCreate = () => openDraft(emptyDraft(pickableEvents()[0]?.key ?? ""));

    const draftFrom = (source: HandlerSource, imported: boolean): Draft => ({
        id: source.id,
        name: source.name,
        eventKey: source.eventKey,
        isEnabled: source.isEnabled,
        cooldownSeconds: source.cooldownSeconds,
        runWhilePossessed: source.runWhilePossessed,
        condition: parseCondition(source.conditionJson),
        schedule: isScheduled(source.eventKey) ? parseSchedule(source.scheduleJson) : defaultSchedule(),
        imported,
        actions: source.actions.map(action => {
            const config = parseConfig(action.configJson);
            const embed = config.embed;
            return {
                ...emptyAction(),
                actionType: action.actionType,
                webhookId: action.webhookId,
                missingWebhookName: action.actionType === ACTION_WEBHOOK && action.webhookId == null ? action.webhookName : null,
                useEmbed: embed != null,
                content: config.content ?? "",
                title: embed?.title ?? "",
                description: embed?.description ?? "",
                url: embed?.url ?? "",
                color: embed?.color ?? "#5865F2",
                fields: (embed?.fields ?? []).map((f: any) => ({ name: f.name ?? "", value: f.value ?? "", inline: !!f.inline })),
                authorName: embed?.authorName ?? "",
                authorUrl: embed?.authorUrl ?? "",
                authorIconUrl: embed?.authorIconUrl ?? "",
                thumbnailUrl: embed?.thumbnailUrl ?? "",
                imageUrl: embed?.imageUrl ?? "",
                footerText: embed?.footerText ?? "",
                footerIconUrl: embed?.footerIconUrl ?? "",
                timestamp: !!embed?.timestamp,
                body: config.body ?? DEFAULT_DISCORD_BODY,
                plugin: config.plugin ?? "",
                task: config.task ?? "",
                startImmediately: config.startImmediately ?? false,
                args: config.arguments ?? {},
            };
        }),
    });

    const openEdit = (handler: EventHandlerDto) => openDraft(draftFrom({
        ...handler,
        actions: handler.actions.map(a => ({ ...a, webhookId: a.webhookId ?? null, webhookName: a.webhookName ?? null })),
    }, false));

    const shareCodeFor = (handler: EventHandlerDto) => encodeShareCode({
        name: handler.name,
        eventKey: handler.eventKey,
        cooldownSeconds: handler.cooldownSeconds,
        runWhilePossessed: handler.runWhilePossessed,
        conditionJson: handler.conditionJson,
        scheduleJson: handler.scheduleJson,
        actions: handler.actions.map(a => ({ actionType: a.actionType, configJson: a.configJson, webhookName: a.webhookName ?? null })),
    });

    const copyShareCode = async (handler: EventHandlerDto) => {
        try {
            await navigator.clipboard.writeText(shareCodeFor(handler));
            toast?.success(t("AUTOMATION_SHARE_COPIED"));
        } catch {
            toast?.error(t("AUTOMATION_SHARE_COPY_FAILED"));
        }
    };

    const openImport = () => {
        setImportCode("");
        setImportError(null);
        setImportOpen(true);
    };

    // A shared webhook is matched to one of the importer's own by name; anything else stays unpicked.
    const runImport = () => {
        const decoded = decodeShareCode(importCode());
        if (!decoded.ok) {
            setImportError(t(decoded.errorKey));
            return;
        }

        const shared = decoded.handler;
        if (!definitionFor(shared.eventKey)) {
            setImportError(t("AUTOMATION_IMPORT_UNKNOWN_EVENT"));
            return;
        }

        const ownWebhooks = webhooks() ?? [];
        setImportOpen(false);
        openDraft(draftFrom({
            ...shared,
            id: null,
            isEnabled: true,
            actions: shared.actions.map(action => ({
                actionType: action.actionType as EventActionType,
                webhookName: action.webhookName,
                webhookId: action.actionType === ACTION_WEBHOOK
                    ? ownWebhooks.find(w => w.name === action.webhookName)?.id ?? null
                    : null,
                configJson: action.configJson,
            })),
        }, true));
    };

    const switchTrigger = (trigger: Trigger) => {
        const current = draft();
        if (trigger === "schedule" && !draftScheduled()) {
            setLastEventKey(current.eventKey);
            setDraft({ ...current, eventKey: scheduleEventKey(), condition: emptyGroup() });
        } else if (trigger === "event" && draftScheduled()) {
            setDraft({ ...current, eventKey: lastEventKey() || (pickableEvents()[0]?.key ?? ""), condition: emptyGroup() });
        }
        setSamples({});
    };

    const toRequestActions = (): EventHandlerActionRequest[] =>
        draft().actions.map((action, index) => ({
            sortOrder: index,
            actionType: action.actionType,
            webhookId: action.actionType === ACTION_WEBHOOK ? action.webhookId : null,
            configJson: JSON.stringify(
                action.actionType !== ACTION_WEBHOOK
                    ? { plugin: action.plugin, task: action.task, arguments: action.args, startImmediately: action.startImmediately }
                    : action.useEmbed
                        ? {
                              contentType: "application/json",
                              content: action.content,
                              embed: {
                                  title: action.title,
                                  description: action.description,
                                  url: action.url,
                                  color: action.color,
                                  fields: action.fields.filter(f => f.name.trim() !== "" || f.value.trim() !== ""),
                                  authorName: action.authorName,
                                  authorUrl: action.authorUrl,
                                  authorIconUrl: action.authorIconUrl,
                                  thumbnailUrl: action.thumbnailUrl,
                                  imageUrl: action.imageUrl,
                                  footerText: action.footerText,
                                  footerIconUrl: action.footerIconUrl,
                                  timestamp: action.timestamp,
                              },
                          }
                        : { body: action.body, contentType: "application/json" }),
        }));

    const save = async () => {
        const current = draft();
        setSaving(true);
        try {
            const scheduled = isScheduled(current.eventKey);
            const body = {
                name: current.name,
                eventKey: current.eventKey,
                isEnabled: current.isEnabled,
                sortOrder: 0,
                cooldownSeconds: scheduled ? 0 : current.cooldownSeconds,
                runWhilePossessed: current.runWhilePossessed,
                conditionJson: requestCondition(current),
                scheduleJson: scheduled ? scheduleToJson(current.schedule) : null,
                actions: toRequestActions(),
            };

            const res = current.id
                ? await api.eventHandler.update(botId(), current.id, body)
                : await api.eventHandler.create(botId(), body);

            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t("AUTOMATION_SAVE_FAILED"));
                return;
            }

            toast?.success(t("AUTOMATION_SAVED"));
            setFormOpen(false);
            await refetch();
        } finally {
            setSaving(false);
        }
    };

    const runDryRun = async (silent = false) => {
        const current = draft();
        const res = await api.eventHandler.dryRun(botId(), {
            eventKey: current.eventKey,
            conditionJson: requestCondition(current),
            scheduleJson: isScheduled(current.eventKey) ? scheduleToJson(current.schedule) : null,
            handlerId: current.id,
            actions: toRequestActions(),
            sampleValues: samples(),
        });

        if (!isOk(res.data)) {
            if (!silent) toast?.error(firstError(res.data) ?? t("AUTOMATION_DRYRUN_FAILED"));
            return;
        }
        setDryRun(res.data.value!);
    };

    // The template parser lives on the server, so a check while the user is still looking at the field
    // has to be a round trip. Debounced, and silent: a toast per keystroke would be unusable.
    createEffect(() => {
        if (!formOpen()) return;

        JSON.stringify(draft());
        JSON.stringify(samples());

        const handle = window.setTimeout(() => void runDryRun(true), 600);
        onCleanup(() => window.clearTimeout(handle));
    });

    const requestCondition = (current: Draft) =>
        isScheduled(current.eventKey) || current.condition.children.length === 0 ? "" : JSON.stringify(current.condition);

    const actionProblem = (index: number) => dryRun()?.actions?.find(a => a.sortOrder === index)?.error ?? null;

    const remove = async (handler: EventHandlerDto) => {
        if (!(await showConfirm(t("AUTOMATION_DELETE_CONFIRM", handler.name), true, { confirmLabel: t("DELETE") }))) return;

        const res = await api.eventHandler.delete(botId(), handler.id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t("AUTOMATION_DELETE_FAILED"));
            return;
        }
        toast?.success(t("AUTOMATION_DELETED"));
        await refetch();
    };

    const updateAction = (index: number, patch: Partial<DraftAction>) => {
        const actions = [...draft().actions];
        actions[index] = { ...actions[index], ...patch };
        setDraft({ ...draft(), actions });
    };

    const updateField = (actionIndex: number, fieldIndex: number, patch: Partial<DraftEmbedField>) => {
        const fields = [...draft().actions[actionIndex].fields];
        fields[fieldIndex] = { ...fields[fieldIndex], ...patch };
        updateAction(actionIndex, { fields });
    };

    const canSave = () => {
        const current = draft();
        if (current.name.trim() === "" || current.eventKey === "") return false;
        if (isScheduled(current.eventKey) && dryRun()?.scheduleError) return false;
        return current.actions.every(action =>
            action.actionType === ACTION_WEBHOOK
                ? action.webhookId != null && action.body.trim() !== ""
                : action.plugin !== "" && action.task !== "");
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <div class={styles.titleBlock}>
                    <h1><Text val="AUTOMATION_TITLE" /></h1>
                    <p class={styles.subtitle}><Text val="AUTOMATION_SUBTITLE" /></p>
                </div>
                <div class={styles.headerActions}>
                    <button class="btn btn-secondary" disabled={(catalog()?.length ?? 0) === 0} onClick={openImport}>
                        <i class="fa-solid fa-file-import" /> <Text val="AUTOMATION_IMPORT" />
                    </button>
                    <button class="btn btn-primary" disabled={(catalog()?.length ?? 0) === 0} onClick={openCreate}>
                        <i class="fa-solid fa-plus" /> <Text val="AUTOMATION_NEW" />
                    </button>
                </div>
            </div>

            <Show
                when={handlers.state !== "pending"}
                fallback={<div class={styles.list}><For each={Array(3).fill(0)}>{() => <div class={styles.skeletonCard} />}</For></div>}
            >
                <Show
                    when={(handlers()?.length ?? 0) > 0}
                    fallback={
                        <EmptyState
                            illustration="jobs"
                            title={t("AUTOMATION_EMPTY_TITLE")}
                            hint={t("AUTOMATION_EMPTY_HINT")}
                            action={
                                <div class={styles.emptyActions}>
                                    <button class="btn btn-secondary" onClick={openImport}>
                                        <i class="fa-solid fa-file-import" /> <Text val="AUTOMATION_IMPORT" />
                                    </button>
                                    <button class="btn btn-primary" onClick={openCreate}>
                                        <i class="fa-solid fa-plus" /> <Text val="AUTOMATION_NEW" />
                                    </button>
                                </div>
                            }
                        />
                    }
                >
                    <div class={styles.list}>
                        <For each={handlers()!}>
                            {(handler) => (
                                <div class={`fcard ${styles.card}`}>
                                    <div class={styles.cardHead}>
                                        <div class={styles.cardTitle}>
                                            <span class={styles.cardName}>{handler.name}</span>
                                            <Badge tone={handler.isEnabled ? "success" : "neutral"} pill>
                                                <Text val={handler.isEnabled ? "AUTOMATION_ENABLED" : "AUTOMATION_DISABLED"} />
                                            </Badge>
                                            <Badge tone={isScheduled(handler.eventKey) ? "accent" : "info"} pill>
                                                <Show when={isScheduled(handler.eventKey)}><i class="fa-solid fa-clock" />{" "}</Show>
                                                {t(`EVENT_${handler.eventKey.toUpperCase()}`)}
                                            </Badge>
                                        </div>
                                        <div class={styles.cardActions}>
                                            <button class="btn btn-secondary btn-sm" onClick={() => setSharing(handler)}>
                                                <i class="fa-solid fa-share-nodes" /> <Text val="AUTOMATION_SHARE" />
                                            </button>
                                            <button class="btn btn-secondary btn-sm" onClick={() => openEdit(handler)}>
                                                <i class="fa-solid fa-pen-to-square" /> <Text val="EDIT" />
                                            </button>
                                            <button class="btn btn-danger btn-sm" onClick={() => void remove(handler)}>
                                                <i class="fa-solid fa-trash" /> <Text val="DELETE" />
                                            </button>
                                        </div>
                                    </div>
                                    <Show
                                        when={isScheduled(handler.eventKey)}
                                        fallback={
                                            <span class={styles.cardSummary}>
                                                {t("AUTOMATION_CARD_SUMMARY", handler.actions.length, handler.cooldownSeconds)}
                                            </span>
                                        }
                                    >
                                        <div class={styles.cardMeta}>
                                            <span class={styles.cardSummary}>
                                                {describeSchedule(parseSchedule(handler.scheduleJson), t)}
                                            </span>
                                            <span class={styles.nextRun}>
                                                <i class="fa-regular fa-clock" />{" "}
                                                <Show when={handler.nextRunAt} fallback={<Text val="AUTOMATION_NEXT_RUN_PAUSED" />}>
                                                    {(nextRunAt) => t("AUTOMATION_NEXT_RUN", formatDateTimeShort(nextRunAt()))}
                                                </Show>
                                            </span>
                                        </div>
                                    </Show>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </Show>

            <div class="fcard v" style={{ gap: "var(--spacing-md)" }}>
                <div class={styles.sectionHeader}>
                    <h2><Text val="AUTOMATION_LOG_TITLE" /></h2>
                    <button class="btn btn-secondary btn-sm" onClick={() => void refetchLog()}>
                        <i class="fa-solid fa-rotate" /> <Text val="AUTOMATION_LOG_REFRESH" />
                    </button>
                </div>

                <Show
                    when={(executions()?.length ?? 0) > 0}
                    fallback={<span class={styles.fieldHint}><Text val="AUTOMATION_LOG_EMPTY" /></span>}
                >
                    <div class="v" style={{ gap: "var(--spacing-sm)" }}>
                        <For each={executions()!}>
                            {(row) => (
                                <div class={styles.logRow}>
                                    <span class={styles.logWhen}>{formatDateTime(row.createdAt)}</span>
                                    <span>{row.handlerName}</span>
                                    <Badge tone={statusTone(row.status)} pill>
                                        {t(`AUTOMATION_STATUS_${row.status}`)}
                                    </Badge>
                                    <Show when={row.error}><span class={styles.logError}>{row.error}</span></Show>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </div>

            <Modal
                open={formOpen()}
                onClose={() => setFormOpen(false)}
                title={draft().id ? t("AUTOMATION_EDIT_TITLE") : t("AUTOMATION_NEW_TITLE")}
                maxWidth="46rem"
                busy={saving()}
            >
                <form class="v" style={{ gap: "var(--spacing-lg)" }} onSubmit={(e) => { e.preventDefault(); void save(); }}>
                    <Show when={draft().imported}>
                        <div class={styles.notice}>
                            <i class="fa-solid fa-file-import" />
                            <span><Text val="AUTOMATION_IMPORTED_NOTICE" /></span>
                        </div>
                    </Show>

                    <div class={styles.fieldRow}>
                        <label class={styles.field}>
                            <span><Text val="AUTOMATION_NAME" /></span>
                            <input
                                type="text"
                                maxLength={200}
                                value={draft().name}
                                onInput={(e) => setDraft({ ...draft(), name: e.currentTarget.value })}
                            />
                        </label>
                        <div class={styles.field}>
                            <span><Text val="AUTOMATION_TRIGGER" /></span>
                            <DateRangePills<Trigger>
                                value={draftScheduled() ? "schedule" : "event"}
                                onChange={switchTrigger}
                                pills={[
                                    { value: "event", labelKey: "AUTOMATION_TRIGGER_EVENT" },
                                    { value: "schedule", labelKey: "AUTOMATION_TRIGGER_SCHEDULE" },
                                ]}
                            />
                        </div>
                    </div>

                    <Show when={!draftScheduled()}>
                        <div class={styles.fieldRow}>
                            <div class={styles.field}>
                                <span><Text val="AUTOMATION_EVENT" /></span>
                                <EventPicker
                                    events={pickableEvents()}
                                    value={draft().eventKey}
                                    onChange={(eventKey) => setDraft({ ...draft(), eventKey, condition: emptyGroup() })}
                                />
                            </div>
                            <label class={styles.field}>
                                <span><Text val="AUTOMATION_COOLDOWN" /></span>
                                <input
                                    type="number"
                                    min={0}
                                    value={draft().cooldownSeconds}
                                    onInput={(e) => setDraft({ ...draft(), cooldownSeconds: Number(e.currentTarget.value) || 0 })}
                                />
                                <span class={styles.fieldHint}><Text val="AUTOMATION_COOLDOWN_HINT" /></span>
                            </label>
                        </div>

                        <Show when={definitionFor(draft().eventKey)?.pluginEnabled === false}>
                            <div class={styles.warning}>
                                <i class="fa-solid fa-triangle-exclamation" />
                                <span>{t("AUTOMATION_PLUGIN_DISABLED", definitionFor(draft().eventKey)?.plugin ?? "")}</span>
                            </div>
                        </Show>

                        <Show when={definitionFor(draft().eventKey)?.observeOnly}>
                            <div class={styles.warning}>
                                <i class="fa-solid fa-circle-info" />
                                <span><Text val="AUTOMATION_OBSERVE_ONLY" /></span>
                            </div>
                        </Show>
                    </Show>

                    <div class={styles.checkRow}>
                        <label class={styles.checkField}>
                            <input
                                type="checkbox"
                                checked={draft().isEnabled}
                                onChange={(e) => setDraft({ ...draft(), isEnabled: e.currentTarget.checked })}
                            />
                            <Text val="AUTOMATION_IS_ENABLED" />
                        </label>
                        <label class={styles.checkField}>
                            <input
                                type="checkbox"
                                checked={draft().runWhilePossessed}
                                onChange={(e) => setDraft({ ...draft(), runWhilePossessed: e.currentTarget.checked })}
                            />
                            <Text val="AUTOMATION_RUN_WHILE_POSSESSED" />
                        </label>
                    </div>

                    <Show
                        when={draftScheduled()}
                        fallback={
                            <div class="v" style={{ gap: "var(--spacing-sm)" }}>
                                <div class={styles.sectionHeader}>
                                    <h2><Text val="AUTOMATION_CONDITIONS" /></h2>
                                </div>
                                <ConditionTreeEditor
                                    node={draft().condition}
                                    params={conditionParams()}
                                    botId={botId()}
                                    onChange={(next) => setDraft({ ...draft(), condition: next })}
                                />
                            </div>
                        }
                    >
                        <div class="v" style={{ gap: "var(--spacing-sm)" }}>
                            <div class={styles.sectionHeader}>
                                <h2><Text val="AUTOMATION_SCHEDULE" /></h2>
                            </div>
                            <ScheduleEditor
                                value={draft().schedule}
                                onChange={(schedule) => setDraft({ ...draft(), schedule })}
                                upcomingRuns={dryRun()?.upcomingRuns ?? []}
                                error={dryRun()?.scheduleError ?? null}
                                previewIncludesJitter={draft().id != null}
                            />
                        </div>
                    </Show>

                    <div class="v" style={{ gap: "var(--spacing-sm)" }}>
                        <div class={styles.sectionHeader}>
                            <h2><Text val="AUTOMATION_ACTIONS" /></h2>
                            <button
                                type="button"
                                class="btn btn-secondary btn-sm"
                                onClick={() => setDraft({ ...draft(), actions: [...draft().actions, emptyAction()] })}
                            >
                                <i class="fa-solid fa-plus" /> <Text val="AUTOMATION_ACTION_ADD" />
                            </button>
                        </div>

                        <Show when={loopWarning()}>
                            <div class={styles.warning}>
                                <i class="fa-solid fa-triangle-exclamation" />
                                <span><Text val="AUTOMATION_LOOP_WARNING" /></span>
                            </div>
                        </Show>

                        <Index each={draft().actions}>
                            {(action, index) => {
                                type TemplateElement = HTMLInputElement | HTMLTextAreaElement;
                                let templateField: HTMLTextAreaElement | undefined;
                                let insertionTarget: { element: TemplateElement; write: (next: string) => void } | undefined;

                                const tracksInsertion = (write: (next: string) => void) =>
                                    (e: FocusEvent & { currentTarget: TemplateElement }) => { insertionTarget = { element: e.currentTarget, write }; };

                                const embedInput = (labelKey: string, key: EmbedTextKey, placeholder?: string) => (
                                    <label class={styles.field}>
                                        <span><Text val={labelKey} /></span>
                                        <input
                                            type="text"
                                            placeholder={placeholder}
                                            value={action()[key]}
                                            onFocus={tracksInsertion(next => updateAction(index, { [key]: next }))}
                                            onInput={(e) => updateAction(index, { [key]: e.currentTarget.value })}
                                        />
                                    </label>
                                );

                                // The caret survives the click on a chip, so the placeholder lands where the author was typing.
                                // Until a field was focused it goes to the main template; a field removed since then no longer counts.
                                const insertPlaceholder = (placeholder: string) => {
                                    const target = insertionTarget?.element.isConnected
                                        ? insertionTarget
                                        : templateField && {
                                              element: templateField as TemplateElement,
                                              write: (next: string) => updateAction(index, action().useEmbed ? { description: next } : { body: next }),
                                          };
                                    if (!target) return;

                                    const text = target.element.value;
                                    const start = target.element.selectionStart ?? text.length;
                                    const end = target.element.selectionEnd ?? start;
                                    target.write(text.slice(0, start) + placeholder + text.slice(end));
                                    queueMicrotask(() => {
                                        target.element.focus();
                                        target.element.setSelectionRange(start + placeholder.length, start + placeholder.length);
                                    });
                                };

                                return (
                                    <div class={styles.action}>
                                        <div class={styles.actionHead}>
                                            <select
                                                value={String(action().actionType)}
                                                onChange={(e) => updateAction(index, { actionType: Number(e.currentTarget.value) as EventActionType })}
                                            >
                                                <option value={String(ACTION_WEBHOOK)}>{t("AUTOMATION_ACTION_WEBHOOK")}</option>
                                                <option value={String(ACTION_RUN_TASK)}>{t("AUTOMATION_ACTION_RUNTASK")}</option>
                                            </select>
                                            <Show when={draft().actions.length > 1}>
                                                <button
                                                    type="button"
                                                    class="btn btn-danger btn-sm btn-icon"
                                                    title={t("AUTOMATION_ACTION_REMOVE")}
                                                    aria-label={t("AUTOMATION_ACTION_REMOVE")}
                                                    onClick={() => setDraft({ ...draft(), actions: draft().actions.filter((_, i) => i !== index) })}
                                                >
                                                    <i class="fa-solid fa-xmark" />
                                                </button>
                                            </Show>
                                        </div>

                                        <Show when={action().actionType === ACTION_WEBHOOK}>
                                            <label class={styles.field}>
                                                <span><Text val="AUTOMATION_ACTION_TARGET" /></span>
                                                <select
                                                    value={action().webhookId ?? ""}
                                                    onChange={(e) => updateAction(index, { webhookId: e.currentTarget.value || null })}
                                                >
                                                    <option value="">{t("AUTOMATION_ACTION_PICK_WEBHOOK")}</option>
                                                    <For each={webhooks() ?? []}>
                                                        {(webhook) => <option value={webhook.id}>{webhook.name}</option>}
                                                    </For>
                                                </select>
                                                <Show when={action().missingWebhookName != null && action().webhookId == null}>
                                                    <span class={styles.fieldHint}>
                                                        {t("AUTOMATION_IMPORTED_WEBHOOK_MISSING", action().missingWebhookName ?? "")}
                                                    </span>
                                                </Show>
                                            </label>

                                            <label class={styles.checkField}>
                                                <input
                                                    type="checkbox"
                                                    checked={action().useEmbed}
                                                    onChange={(e) => updateAction(index, { useEmbed: e.currentTarget.checked })}
                                                />
                                                <Text val="AUTOMATION_ACTION_USE_EMBED" />
                                            </label>

                                            <Show when={!action().useEmbed}>
                                                <label class={styles.field}>
                                                    <span><Text val="AUTOMATION_ACTION_BODY" /></span>
                                                    <textarea
                                                        ref={(field) => { templateField = field; }}
                                                        class={styles.template}
                                                        value={action().body}
                                                        onFocus={tracksInsertion(next => updateAction(index, { body: next }))}
                                                        onInput={(e) => updateAction(index, { body: e.currentTarget.value })}
                                                    />
                                                </label>
                                            </Show>

                                            <Show when={action().useEmbed}>
                                                <label class={styles.field}>
                                                    <span><Text val="AUTOMATION_ACTION_CONTENT" /></span>
                                                    <input
                                                        type="text"
                                                        value={action().content}
                                                        onFocus={tracksInsertion(next => updateAction(index, { content: next }))}
                                                        onInput={(e) => updateAction(index, { content: e.currentTarget.value })}
                                                    />
                                                    <span class={styles.fieldHint}><Text val="AUTOMATION_ACTION_CONTENT_HINT" /></span>
                                                </label>

                                                <div class={styles.embed} style={{ "border-left-color": action().color }}>
                                                    <div class={styles.fieldRow}>
                                                        {embedInput("AUTOMATION_EMBED_AUTHOR_NAME", "authorName")}
                                                        {embedInput("AUTOMATION_EMBED_AUTHOR_ICON", "authorIconUrl", "https://")}
                                                        {embedInput("AUTOMATION_EMBED_AUTHOR_URL", "authorUrl", "https://")}
                                                    </div>

                                                    <div class={styles.fieldRow}>
                                                        <label class={styles.field}>
                                                            <span><Text val="AUTOMATION_EMBED_TITLE" /></span>
                                                            <input
                                                                type="text"
                                                                value={action().title}
                                                                onFocus={tracksInsertion(next => updateAction(index, { title: next }))}
                                                                onInput={(e) => updateAction(index, { title: e.currentTarget.value })}
                                                            />
                                                        </label>
                                                        <label class={styles.field} style={{ flex: "0 0 7rem" }}>
                                                            <span><Text val="AUTOMATION_EMBED_COLOR" /></span>
                                                            <input
                                                                type="color"
                                                                value={action().color}
                                                                onInput={(e) => updateAction(index, { color: e.currentTarget.value })}
                                                            />
                                                        </label>
                                                    </div>

                                                    <label class={styles.field}>
                                                        <span><Text val="AUTOMATION_EMBED_DESCRIPTION" /></span>
                                                        <textarea
                                                            ref={(field) => { templateField = field; }}
                                                            class={styles.template}
                                                            value={action().description}
                                                            onFocus={tracksInsertion(next => updateAction(index, { description: next }))}
                                                            onInput={(e) => updateAction(index, { description: e.currentTarget.value })}
                                                        />
                                                    </label>

                                                    <label class={styles.field}>
                                                        <span><Text val="AUTOMATION_EMBED_URL" /></span>
                                                        <input
                                                            type="text"
                                                            value={action().url}
                                                            onFocus={tracksInsertion(next => updateAction(index, { url: next }))}
                                                            onInput={(e) => updateAction(index, { url: e.currentTarget.value })}
                                                        />
                                                    </label>

                                                    <div class={styles.sectionHeader}>
                                                        <span class={styles.metaLabel}><Text val="AUTOMATION_EMBED_FIELDS" /></span>
                                                        <button
                                                            type="button"
                                                            class="btn btn-secondary btn-sm"
                                                            onClick={() => updateAction(index, { fields: [...action().fields, { name: "", value: "", inline: true }] })}
                                                        >
                                                            <i class="fa-solid fa-plus" /> <Text val="AUTOMATION_EMBED_FIELD_ADD" />
                                                        </button>
                                                    </div>

                                                    <Index each={action().fields}>
                                                        {(field, fieldIndex) => (
                                                            <div class={styles.embedField}>
                                                                <input
                                                                    type="text"
                                                                    placeholder={t("AUTOMATION_EMBED_FIELD_NAME")}
                                                                    value={field().name}
                                                                    onFocus={tracksInsertion(next => updateField(index, fieldIndex, { name: next }))}
                                                                    onInput={(e) => updateField(index, fieldIndex, { name: e.currentTarget.value })}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    placeholder={t("AUTOMATION_EMBED_FIELD_VALUE")}
                                                                    value={field().value}
                                                                    onFocus={tracksInsertion(next => updateField(index, fieldIndex, { value: next }))}
                                                                    onInput={(e) => updateField(index, fieldIndex, { value: e.currentTarget.value })}
                                                                />
                                                                <label class={styles.checkField}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={field().inline}
                                                                        onChange={(e) => updateField(index, fieldIndex, { inline: e.currentTarget.checked })}
                                                                    />
                                                                    <Text val="AUTOMATION_EMBED_FIELD_INLINE" />
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-danger btn-sm btn-icon"
                                                                    title={t("AUTOMATION_EMBED_FIELD_REMOVE")}
                                                                    aria-label={t("AUTOMATION_EMBED_FIELD_REMOVE")}
                                                                    onClick={() => updateAction(index, { fields: action().fields.filter((_, i) => i !== fieldIndex) })}
                                                                >
                                                                    <i class="fa-solid fa-xmark" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </Index>

                                                    <div class={styles.fieldRow}>
                                                        {embedInput("AUTOMATION_EMBED_IMAGE", "imageUrl", "https://")}
                                                        {embedInput("AUTOMATION_EMBED_THUMBNAIL", "thumbnailUrl", "https://")}
                                                    </div>

                                                    <div class={styles.fieldRow}>
                                                        {embedInput("AUTOMATION_EMBED_FOOTER_TEXT", "footerText")}
                                                        {embedInput("AUTOMATION_EMBED_FOOTER_ICON", "footerIconUrl", "https://")}
                                                    </div>

                                                    <label class={styles.checkField}>
                                                        <input
                                                            type="checkbox"
                                                            checked={action().timestamp}
                                                            onChange={(e) => updateAction(index, { timestamp: e.currentTarget.checked })}
                                                        />
                                                        <Text val="AUTOMATION_EMBED_TIMESTAMP" />
                                                    </label>

                                                    <span class={styles.fieldHint}><Text val="AUTOMATION_EMBED_LINKS_HINT" /></span>
                                                </div>
                                            </Show>

                                            <For each={[
                                                { labelKey: "AUTOMATION_PLACEHOLDERS_EVENT", params: eventParams() },
                                                { labelKey: "AUTOMATION_PLACEHOLDERS_BOT", params: botParams() },
                                            ].filter(group => group.params.length > 0)}>
                                                {(group) => (
                                                    <div class={styles.placeholders}>
                                                        <span class={styles.placeholderLabel}><Text val={group.labelKey} /></span>
                                                        <For each={group.params}>
                                                            {(param) => <PlaceholderChip paramKey={param.key} type={param.type} onInsert={insertPlaceholder} />}
                                                        </For>
                                                    </div>
                                                )}
                                            </For>
                                            <span class={styles.fieldHint}><Text val="AUTOMATION_PLACEHOLDERS_HINT" /></span>
                                        </Show>

                                        <Show when={actionProblem(index)}>
                                            {(problem) => (
                                                <div class={styles.actionProblem}>
                                                    <i class="fa-solid fa-circle-exclamation" />
                                                    <span>{problem()}</span>
                                                </div>
                                            )}
                                        </Show>

                                        <Show when={action().actionType === ACTION_RUN_TASK}>
                                            <div class={styles.fieldRow}>
                                                <label class={styles.field}>
                                                    <span><Text val="AUTOMATION_ACTION_PLUGIN" /></span>
                                                    <select
                                                        value={action().plugin}
                                                        onChange={(e) => updateAction(index, { plugin: e.currentTarget.value, task: "", args: {} })}
                                                    >
                                                        <option value="">{t("AUTOMATION_ACTION_PICK_PLUGIN")}</option>
                                                        <For each={tasks() ?? []}>
                                                            {(group) => <option value={group.plugin}>{group.plugin}</option>}
                                                        </For>
                                                    </select>
                                                </label>
                                                <label class={styles.field}>
                                                    <span><Text val="AUTOMATION_ACTION_TASK" /></span>
                                                    <select
                                                        value={action().task}
                                                        onChange={(e) => updateAction(index, { task: e.currentTarget.value, args: {} })}
                                                    >
                                                        <option value="">{t("AUTOMATION_ACTION_PICK_TASK")}</option>
                                                        <For each={(tasks() ?? []).find(g => g.plugin === action().plugin)?.tasks ?? []}>
                                                            {(definition) => <option value={definition.name}>{definition.name}</option>}
                                                        </For>
                                                    </select>
                                                </label>
                                            </div>

                                            <For each={(tasks() ?? []).find(g => g.plugin === action().plugin)?.tasks.find(t2 => t2.name === action().task)?.parameters ?? []}>
                                                {(parameter) => (
                                                    <label class={styles.field}>
                                                        <span title={parameter.description ?? undefined}>{parameter.name}{parameter.required ? " *" : ""}</span>
                                                        <TaskParameterInput
                                                            botId={botId()}
                                                            parameter={parameter}
                                                            acceptsPlaceholders
                                                            value={action().args[parameter.name] ?? ""}
                                                            onChange={(value) => updateAction(index, { args: { ...action().args, [parameter.name]: value } })}
                                                        />
                                                    </label>
                                                )}
                                            </For>

                                            <label class={styles.checkField}>
                                                <input
                                                    type="checkbox"
                                                    checked={action().startImmediately}
                                                    onChange={(e) => updateAction(index, { startImmediately: e.currentTarget.checked })}
                                                />
                                                <Text val="AUTOMATION_ACTION_START_NOW" />
                                            </label>
                                        </Show>
                                    </div>
                                );
                            }}
                        </Index>
                    </div>

                    <div class="v" style={{ gap: "var(--spacing-sm)" }}>
                        <div class={styles.sectionHeader}>
                            <h2><Text val="AUTOMATION_DRYRUN" /></h2>
                            <button type="button" class="btn btn-secondary btn-sm" onClick={() => void runDryRun()}>
                                <i class="fa-solid fa-flask" /> <Text val="AUTOMATION_DRYRUN_RUN" />
                            </button>
                        </div>
                        <span class={styles.fieldHint}><Text val="AUTOMATION_DRYRUN_HINT" /></span>

                        <div class={styles.fieldRow}>
                            <For each={eventParams()}>
                                {(param) => (
                                    <label class={styles.field}>
                                        <span>{param.key}</span>
                                        <input
                                            type="text"
                                            value={samples()[param.key] ?? ""}
                                            onInput={(e) => setSamples({ ...samples(), [param.key]: e.currentTarget.value })}
                                        />
                                    </label>
                                )}
                            </For>
                        </div>

                        <Show when={dryRun()}>
                            {(result) => (
                                <div class="v" style={{ gap: "var(--spacing-sm)" }}>
                                    <span class={result().matched ? styles.matched : styles.unmatched}>
                                        <i class={`fa-solid ${result().matched ? "fa-circle-check" : "fa-circle-minus"}`} />{" "}
                                        <Text val={result().matched ? "AUTOMATION_DRYRUN_MATCHED" : "AUTOMATION_DRYRUN_UNMATCHED"} />
                                    </span>
                                    <Show when={result().conditionError}>
                                        <span class={styles.logError}>{result().conditionError}</span>
                                    </Show>
                                    <For each={result().actions}>
                                        {(preview) => (
                                            <div class={styles.preview}>
                                                <Show when={preview.error} fallback={preview.rendered}>
                                                    <span class={styles.logError}>{preview.error}</span>
                                                </Show>
                                            </div>
                                        )}
                                    </For>
                                </div>
                            )}
                        </Show>
                    </div>

                    <div class={styles.formActions}>
                        <button type="button" class="btn btn-secondary" onClick={() => setFormOpen(false)}>
                            <Text val="CANCEL" />
                        </button>
                        <button type="submit" class="btn btn-primary" disabled={saving() || !canSave()}>
                            <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE" /></>}>
                                <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE" />
                            </Show>
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                open={sharing() != null}
                onClose={() => setSharing(null)}
                title={t("AUTOMATION_SHARE_TITLE", sharing()?.name ?? "")}
                maxWidth="36rem"
            >
                <Show when={sharing()}>
                    {(handler) => (
                        <div class="v" style={{ gap: "var(--spacing-md)" }}>
                            <p class={styles.modalText}><Text val="AUTOMATION_SHARE_HINT" /></p>
                            <textarea
                                class={styles.shareCode}
                                readOnly
                                value={shareCodeFor(handler())}
                                aria-label={t("AUTOMATION_SHARE_CODE")}
                                onFocus={(e) => e.currentTarget.select()}
                            />
                            <div class={styles.formActions}>
                                <button type="button" class="btn btn-secondary" onClick={() => setSharing(null)}>
                                    <Text val="CLOSE" />
                                </button>
                                <button type="button" class="btn btn-primary" onClick={() => void copyShareCode(handler())}>
                                    <i class="fa-solid fa-copy" /> <Text val="AUTOMATION_SHARE_COPY" />
                                </button>
                            </div>
                        </div>
                    )}
                </Show>
            </Modal>

            <Modal open={importOpen()} onClose={() => setImportOpen(false)} title={t("AUTOMATION_IMPORT_TITLE")} maxWidth="36rem">
                <form class="v" style={{ gap: "var(--spacing-md)" }} onSubmit={(e) => { e.preventDefault(); runImport(); }}>
                    <p class={styles.modalText}><Text val="AUTOMATION_IMPORT_HINT" /></p>
                    <label class={styles.field}>
                        <span><Text val="AUTOMATION_SHARE_CODE" /></span>
                        <textarea
                            class={styles.shareCode}
                            value={importCode()}
                            placeholder="vektor-handler:..."
                            onInput={(e) => { setImportCode(e.currentTarget.value); setImportError(null); }}
                        />
                    </label>
                    <Show when={importError()}>
                        {(error) => (
                            <div class={styles.actionProblem}>
                                <i class="fa-solid fa-circle-exclamation" />
                                <span>{error()}</span>
                            </div>
                        )}
                    </Show>
                    <div class={styles.formActions}>
                        <button type="button" class="btn btn-secondary" onClick={() => setImportOpen(false)}>
                            <Text val="CANCEL" />
                        </button>
                        <button type="submit" class="btn btn-primary" disabled={importCode().trim() === ""}>
                            <i class="fa-solid fa-pen-to-square" /> <Text val="AUTOMATION_IMPORT_OPEN" />
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

function statusTone(status: number): "success" | "error" | "warning" | "neutral" {
    if (status === 0) return "success";
    if (status === 1) return "error";
    if (status === 5 || status === 6) return "warning";
    return "neutral";
}

function parseCondition(conditionJson: string): ConditionGroup {
    if (!conditionJson) return emptyGroup();
    try {
        const parsed = JSON.parse(conditionJson);
        return parsed && typeof parsed === "object" && "group" in parsed ? parsed as ConditionGroup : emptyGroup();
    } catch {
        return emptyGroup();
    }
}

function parseConfig(configJson: string): Record<string, any> {
    try {
        return JSON.parse(configJson || "{}") ?? {};
    } catch {
        return {};
    }
}
